import {Button, Flex, Input, Modal, Segmented, Table, TableColumnsType} from 'antd'
import {useEffect, useRef, useState} from "react";
import {useSearchParams} from "react-router";
import {DataEntryParent} from "../components/data-entry-parent"
import {configDeliveryService} from "../components/api";
import {ErrorView} from "../components/show-error";
import {ContainerStatusesResponse_Info} from "../api/ga/config_manager/configdelivery/config_delivery_pb";

interface DataType {
  key: string
  service: string
  name: string
  label: string
  image: string
  id: string
  idOld: string
  state: number
}

export default function ServiceConfigDeployment() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [env, setEnv] = useState(searchParams.get('env') ?? 'dev-2')
  const [branch, setBranch] = useState(searchParams.get('configs_branch') ?? 'master')
  const [availableServicesData, setAvailableServicesData] = useState([] as string[])
  const [historyData, setHistoryData] = useState([] as ContainerStatusesResponse_Info[])
  const [idOldMap, setIdOldMap] = useState({} as Record<string, string>)
  const [services, setServices] = useState([] as DataType[])
  const [autoUpdate, setAutoUpdate] = useState<'auto' | 'off'>('off')
  const [downloading, setDownloading] = useState('')
  const [creating, setCreating] = useState('')
  const [modal, contextHolder] = Modal.useModal()
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchHistory = (currentEnv: string) => {
    configDeliveryService.containerStatuses({env: currentEnv})
      .then((resp) => {
        setHistoryData(resp.infos)
      })
      .catch((e: any) => { ErrorView(modal, 'history error', e) })
  }

  useEffect(() => {
    configDeliveryService.availableServices({branch: branch})
      .then((resp) => setAvailableServicesData(resp.services ?? []))
      .catch((e: any) => { ErrorView(modal, 'load error', e) })
  }, [branch])

  useEffect(() => {
    fetchHistory(env)
  }, [env])

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current)
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current)
    if (autoUpdate === 'auto') {
      snapshotIds()
      pollRef.current = setInterval(() => fetchHistory(env), 2000)
      stopTimerRef.current = setTimeout(() => setAutoUpdate('off'), 60_000)
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current)
    }
  }, [autoUpdate, env])

  useEffect(() => {
    const byName = new Map(historyData.map(r => [r.name, r]))
    const svcSet = new Set(availableServicesData)

    const rows: DataType[] = availableServicesData.map(svc => {
      const h = byName.get(svc)
      return {
        key: svc,
        service: svc,
        name: h?.name ?? '',
        label: h?.label ?? '',
        image: h?.image ?? '',
        id: h?.id ?? '',
        idOld: idOldMap[svc] ?? '',
        state: h?.state ?? -1,
      } as DataType
    })

    historyData.forEach(r => {
      if (!svcSet.has(r.name)) {
        rows.push({
          key: r.id ?? r.name,
          service: '',
          name: r.name,
          label: r.label,
          image: r.image,
          id: r.id,
          idOld: idOldMap[r.name] ?? '',
          state: r.state,
        } as DataType)
      }
    })

    rows.sort((a, b) => {
      if (!a.service && b.service) return 1
      if (a.service && !b.service) return -1
      return a.service.localeCompare(b.service) || a.name.localeCompare(b.name)
    })
    setServices(rows)
  }, [availableServicesData, historyData, idOldMap])

  const snapshotIds = () => setIdOldMap(prev => {
    const next = {...prev}
    historyData.forEach(s => { if (s.id) next[s.name] = s.id })
    return next
  })

  // Save the configs archive as a file
  const saveArchive = (body: Uint8Array, service: string, source: string) => {
    const blob = new Blob([new Uint8Array(body)], {type: 'application/octet-stream'})
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${service}-${env}-configs-${source}.txz`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  // Fetch obfuscated configs archive from s3 and save it as a file
  const downloadObfuscatedConfigs = (record: DataType) => {
    const service = record.service
    if (!env.trim()) {
      modal.warning({title: 'env is empty', content: 'Specify env before downloading configs'})
      return
    }
    setDownloading(record.key)
    configDeliveryService.downloadObfuscatedConfigs({env: env, service: service})
      .then((resp) => saveArchive(resp.body, service, 'download'))
      .catch((e: any) => { ErrorView(modal, 'download error', e) })
      .finally(() => setDownloading(''))
  }

  // Build obfuscated configs archive on the backend and save it as a file
  const createObfuscatedConfigs = (record: DataType) => {
    const service = record.service
    if (!env.trim()) {
      modal.warning({title: 'env is empty', content: 'Specify env before creating configs'})
      return
    }
    setCreating(record.key)
    configDeliveryService.createObfuscatedConfigs({env: env, service: service, branch: branch})
      .then((resp) => saveArchive(resp.body, service, 'create'))
      .catch((e: any) => { ErrorView(modal, 'create error', e) })
      .finally(() => setCreating(''))
  }

  const columns: TableColumnsType<DataType> = [
    {
      title: 'service',
      dataIndex: 'service',
      key: 'service',
    },
    {
      title: 'container name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'image',
      dataIndex: 'image',
      key: 'image',
    },
    {
      title: 'container id',
      dataIndex: 'id',
      key: 'id',
      render: (_, record) => {
        const text = record.id?.length > 5 ? `...${record.id.slice(-5)}` : record.id
        const changed = record.id && record.idOld && record.id !== record.idOld
        return <span style={changed ? {color: '#52c41a', fontWeight: 600} : undefined}>{text}</span>
      },
    },
    {
      title: 'container id_old',
      dataIndex: 'idOld',
      key: 'idOld',
      render: (_, record) => record.idOld?.length > 5 ? `...${record.idOld.slice(-5)}` : record.idOld,
    },
    {
      title: 'container state',
      dataIndex: 'state',
      key: 'state',
      render: (state: number) => {
        switch (state) {
          case 0: return 'waiting'
          case 1: return 'running'
          case 2: return 'terminated'
          default: return state
        }
      },
    },
    {
      title: 'actions',
      key: 'actions',
      render: (_, record) => (
        <>
          {record.service ? (
            <Button
              size="small"
              loading={creating === record.key}
              onClick={() => createObfuscatedConfigs(record)}
            >
              create obfuscated configs
            </Button>
          ) : null}
          {record.service ? (
            <Button
              size="small"
              loading={downloading === record.key}
              onClick={() => downloadObfuscatedConfigs(record)}
            >
              download obfuscated configs from s3
            </Button>
          ) : null}
          {record.service && record.name ? (
            <Button
              size="small"
              onClick={() => {
                modal.confirm({
                  title: 'Deploy config',
                  content: `Deploy config for service "${record.service}" to env "${env}"?`,
                  onOk: () => {
                    configDeliveryService.apply({env: env, service: record.service, branch: branch})
                      .then(() => {
                        setAutoUpdate('auto')
                        modal.success({title: 'Config deployed', content: `Service: ${record.service}`})
                      })
                      .catch((e: any) => { ErrorView(modal, 'deploy error', e) })
                  },
                })
              }}
            >
              deploy config
            </Button>
          ) : null}
        </>
      ),
    },
  ]

  return (
    <>
      <div style={{maxWidth: 1400, margin: '0 auto', padding: '24px 24px 40px', textAlign: 'left'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 0}}>
        <h1 style={{fontSize: 32, fontWeight: 900, color: 'var(--type-primary)', margin: 0}}>
          Service Config Deployment
        </h1>
      </div>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 16, marginBottom: 16, alignItems: 'start'}}>
        <DataEntryParent text="branch">
          <Input
            value={branch}
            placeholder="branch"
            onChange={(e) => {
              const v = e.target.value
              setBranch(v)
              setSearchParams((p) => { if (v) p.set('configs_branch', v); else p.delete('configs_branch'); return p })
            }}
          />
        </DataEntryParent>
        <DataEntryParent text="env">
          <Input
            value={env}
            placeholder="env"
            onChange={(e) => {
              const v = e.target.value
              setEnv(v)
              setSearchParams((p) => { if (v) p.set('env', v); else p.delete('env'); return p })
            }}
          />
        </DataEntryParent>
        <DataEntryParent text="update">
          <Flex gap={8}>
            <Segmented
              options={[{label: <span style={autoUpdate === 'auto' ? {color: '#52c41a'} : undefined}>auto</span>, value: 'auto'}, {label: 'off', value: 'off'}]}
              value={autoUpdate}
              onChange={(v) => setAutoUpdate(v as 'auto' | 'off')}
            />
            <Button onClick={() => { snapshotIds(); fetchHistory(env) }}>update</Button>
          </Flex>
        </DataEntryParent>
      </div>
      <div style={{background: 'var(--surface-white)', borderRadius: 'var(--radius-basic)', border: '1px solid var(--border)', overflow: 'hidden'}}>
      <Table
        dataSource={services}
        columns={columns}
        size="small"
        pagination={{defaultPageSize: 100}}
      />
      </div>
      </div>
      {contextHolder}
    </>
  )
}